<script setup lang="ts">
import type {Messages, NavigationMenuItem} from '@nuxt/ui'
import {Notification, Notivue} from 'notivue'
import {defineLocale} from '@nuxt/ui/composables'
import {computed, onMounted, ref} from "vue";
import {useAuthStore} from "@/store/auth-store.ts";
import {ButtonAction} from "@/utils/ButtonAction.ts";
import {api} from "@/services/api.ts";
import {adminSideBarLinks, baseLinks, studentSideBarLinks} from "@/utils/constants/common.constants.ts";
import {ShortcutUtils} from "@/utils/ShortcutUtils.ts";
import {useRoute} from "vue-router";
import {useCommonStore} from "@/store/common-store.ts";
import CommonUtils from "@/utils/CommonUtils.ts";

const locale = defineLocale<Messages>({
  name: 'العربية',
  code: 'ar',
  dir: 'rtl',
  messages: {
    alert: {
      close: 'إغلاق'
    },
    authForm: {
      hidePassword: 'إخفاء كلمة المرور',
      showPassword: 'إظهار كلمة المرور',
      submit: 'متابعة'
    },
    banner: {
      close: 'إغلاق'
    },
    calendar: {
      nextMonth: 'الشهر المقبل',
      nextYear: 'السنة المقبلة',
      prevMonth: 'الشهر السابق',
      prevYear: 'السنة السابقة'
    },
    carousel: {
      dots: 'اختر الشريحة المراد عرضها',
      goto: 'الذهاب إلى شريحة {slide}',
      next: 'التالي',
      prev: 'السابق'
    },
    chatPrompt: {
      placeholder: 'اكتب رسالتك هنا…'
    },
    chatPromptSubmit: {
      label: 'إرسال'
    },
    colorMode: {
      dark: 'داكن',
      light: 'فاتح',
      switchToDark: 'التبديل إلى الوضع الداكن',
      switchToLight: 'التبديل إلى الوضع الفاتح',
      system: 'النظام'
    },
    commandPalette: {
      back: 'رجوع',
      close: 'إغلاق',
      noData: 'لا توجد بيانات',
      noMatch: 'لا توجد نتائج مطابقة',
      placeholder: 'اكتب أمرًا أو ابحث…'
    },
    contentSearch: {
      links: 'الروابط',
      theme: 'السمة'
    },
    contentSearchButton: {
      label: 'بحث…'
    },
    contentToc: {
      title: 'في هذه الصفحة'
    },
    dashboardSearch: {
      theme: 'السمة'
    },
    dashboardSearchButton: {
      label: 'بحث…'
    },
    dashboardSidebarCollapse: {
      collapse: 'طي الشريط الجانبي',
      expand: 'توسيع الشريط الجانبي'
    },
    dashboardSidebarToggle: {
      close: 'إغلاق الشريط الجانبي',
      open: 'فتح الشريط الجانبي'
    },
    error: {
      clear: 'العودة إلى الصفحة الرئيسية'
    },
    fileUpload: {
      removeFile: 'إزالة {filename}'
    },
    header: {
      close: 'إغلاق القائمة',
      open: 'فتح القائمة'
    },
    inputMenu: {
      create: 'إنشاء "{label}"',
      noData: 'لا توجد بيانات',
      noMatch: 'لا توجد نتائج مطابقة'
    },
    inputNumber: {
      decrement: 'تقليل',
      increment: 'زيادة'
    },
    modal: {
      close: 'إغلاق'
    },
    pricingTable: {
      caption: 'مقارنة الخطط السعرية'
    },
    prose: {
      codeCollapse: {
        closeText: 'طي',
        name: 'كود',
        openText: 'توسيع'
      },
      collapsible: {
        closeText: 'إخفاء',
        name: 'خصائص',
        openText: 'إظهار'
      },
      pre: {
        copy: 'نسخ الكود إلى الحافظة'
      }
    },
    selectMenu: {
      create: 'إنشاء "{label}"',
      noData: 'لا توجد بيانات',
      noMatch: 'لا توجد نتائج مطابقة',
      search: 'بحث…'
    },
    slideover: {
      close: 'إغلاق'
    },
    table: {
      noData: 'لا توجد بيانات'
    },
    toast: {
      close: 'إغلاق'
    }
  }
})
const items = computed<NavigationMenuItem[][]>(() => {
  if (!useAuthStore().isLoggedIn || !useAuthStore().getUser.isProfileCompleted)
    return [[]]
  const linksToShow: NavigationMenuItem[] = baseLinks;
  if (useAuthStore().isAdminOrSubAdmin)
    linksToShow.push(...adminSideBarLinks);
  else
    linksToShow.push(...studentSideBarLinks);
  return [linksToShow]
})

const appLoaderIsLoading = ref(true)
const isSidebarOpen = ref(false)

const cachedRouters = ["ViewUsersPage", "LessonsPage", "PaymentsPage", "GroupsPage", "DashboardWrapperPage", "GradesPage", "StudentPayments", "StudentAttendance", "StudentGrades"];


onMounted(() => {
  setTimeout(() => {
    appLoaderIsLoading.value = false;
  }, 2900)
})

ShortcutUtils.registerGlobalShortcuts();

const canShowLoader = computed(() => !useAuthStore().isAuthReady || appLoaderIsLoading.value)
const canShowAppFooter = computed(() => !useAuthStore().isLoggedIn)
</script>
<template>
  <UApp :locale="locale">
    <Notivue v-slot="item">
      <Notification :item="item"/>
    </Notivue>
    <Transition
        enter-active-class="transition-transform duration-300"
        leave-active-class="transition-transform duration-[1000]"
        enter-from-class="translate-y-6 opacity-0"
        enter-to-class="translate-y-0 opacity-100"
        leave-from-class="translate-y-0 opacity-100"
        leave-to-class="translate-y-6 opacity-0">
      <AppLoader v-if="canShowLoader" v-model="appLoaderIsLoading"/>
    </Transition>
    <div class="w-full nav-item h-16 fixed bottom-0 left-0 lg:hidden z-10 flex items-center px-2" v-if="useAuthStore().isLoggedIn">
      <UNavigationMenu
          highlight
          :ui="{link:'flex-col md:flex-row',
          linkLabel:useAuthStore().isStudent?'sm:block text-[12px] sm:text-sm':'sm:block text-[10px] sm:text-sm',
          linkLeadingIcon:useAuthStore().isStudent?'size-7':'size-5'}"
          collapsed
          popover
          variant="pill"
          :items="items"
          orientation="horizontal"
          class="w-full justify-around border-t border-default bg-default p-2"
      />
    </div>
    <UDashboardGroup unit="rem">
      <UDashboardSidebar
          :class="{'lg:flex!':useAuthStore().isAuthReady && useAuthStore().isLoggedIn}"
          v-model:open="isSidebarOpen"
          class="bg-elevated/25"
          :ui="{ footer: 'border-t border-default py-6',toggleSide:'hidden',root:'bg-[#FAF9F8] !hidden divide-y divide-black/10',body:'py-8'}">
        <template #header="{ collapsed }">
          <div class="py-4" :class="[collapsed?'px-2':'px-3 lg:px-4']">
            <UUser
                :name="useAuthStore().getDisplayedName ?? 'الطالب'"
                :description="useAuthStore().isAdminOrSubAdmin ? useAuthStore().getUser.role : useAuthStore().getUser.classLevel ?? 'الفصل الدراسي'"
                :avatar="{...useAuthStore().avatar}"
            />
          </div>
        </template>
        <template #default="{ collapsed }">
          <UNavigationMenu
              :collapsed="collapsed"
              tooltip
              :ui="{linkLeadingIcon:'size-7',linkLabel:'text-md'}"
              :items="items[0]"
              orientation="vertical"
              popover
          />
        </template>

        <template #footer>
          <UButton to="/auth/login" v-if="!useAuthStore().isLoggedIn" label="تسجيل الدخول" icon="hugeicons:login-square-01" block/>
          <UButton @click="ButtonAction.performAction(api.auth.logOut)" v-else v-bind="{...ButtonAction.getDisabledButtonProps().value}" block
                   variant="soft"
                   label="تسجيل خروج" icon="streamline-flex:logout-1"/>
        </template>
      </UDashboardSidebar>
      <UDashboardPanel :ui="{body:'p-2.5 gap-2 sm:gap-3 sm:p-2'}" v-if="!canShowLoader">
        <template #header>
          <UDashboardNavbar :class="[useAuthStore().isLoggedIn ? 'hidden lg:flex':'']">
            <template #title>
              <BaseLogo/>
            </template>
            <template #left>
              <BaseLogo :show-description="useAuthStore().isLoggedIn"/>
            </template>
            <template #right>
              <UButton to="/auth/login" v-if="!useAuthStore().isLoggedIn" label="تسجيل الدخول" icon="hugeicons:login-square-01" block/>
            </template>
          </UDashboardNavbar>
        </template>
        <template #body>
          <BaseSectionHeader v-if="useAuthStore().isLoggedIn" :title="CommonUtils.getLessonTitle(useCommonStore().getCurrentLesson)"
                             :text="CommonUtils.getLessonSubtitle(useCommonStore().getCurrentLesson)"/>
          <RouterView v-slot="{ Component }">
            <Transition name="route" mode="out-in">
            <KeepAlive :include="cachedRouters">
              <component :is="Component" />
            </KeepAlive>
            </Transition>
          </RouterView>
        </template>
        <template #footer>
          <AppFooter v-if="canShowAppFooter"/>
          <div class="pt-20 lg:hidden" v-if="useAuthStore().isLoggedIn"/>
        </template>
      </UDashboardPanel>
    </UDashboardGroup>
  </UApp>
</template>

<style>
.route-enter-active,
.route-leave-active {
  transition: all 0.29s ease;
}

.route-enter-from,
.route-leave-to {
  opacity: 0;
  transform: scale(0.98888);
}
</style>